import { useEffect, useState } from 'react';

export interface UseCryptoSupportArgs {
  verbose?: boolean;
}

export interface CryptoSupport {
  algorithms: string[];
  supported: boolean;
  pending: boolean;
  performance?: PerformanceMeasure;
}

export default function useCryptoSupport({
  verbose = false,
}: UseCryptoSupportArgs = {}): CryptoSupport {
  const [cryptoSupported, setCryptoSupported] = useState<CryptoSupport>({
    pending: true,
    supported: false,
    algorithms: [],
  });

  const crypto = typeof window !== 'undefined' ? window.crypto : null;
  const subtle = crypto && crypto.subtle ? crypto.subtle : null;

  useEffect(() => {
    if (!crypto || !subtle) {
      setCryptoSupported({
        pending: false,
        supported: false,
        algorithms: [],
      });

      return;
    }
    let isMounted = true;

    performance.mark('crypto-support-start');

    const generateKeyCheck =
      (
        algorithm: RsaHashedKeyGenParams | EcKeyGenParams | AesKeyGenParams
      ): (() => Promise<CryptoKey | CryptoKeyPair>) =>
      () =>
        subtle.generateKey(algorithm, true, ['encrypt', 'decrypt']);

    const algorithmsToCheck = [
      {
        name: 'A256GCM',
        test: generateKeyCheck({ name: 'AES-GCM', length: 256 }),
      },
      {
        name: 'RSA-OAEP',
        test: generateKeyCheck({
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-1',
        }),
      },
      {
        name: 'RSA-OAEP-256',
        test: generateKeyCheck({
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-256',
        }),
      },
      {
        name: 'RSA-OAEP-384',
        test: generateKeyCheck({
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-384',
        }),
      },
      {
        name: 'RSA-OAEP-512',
        test: generateKeyCheck({
          name: 'RSA-OAEP',
          modulusLength: 2048,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-512',
        }),
      },
      {
        name: 'SHA-256',
        test: async () => subtle.digest('SHA-256', new Uint8Array([1, 2, 3])),
      },
    ];

    new Promise<void>((resolve) => {
      const checks = algorithmsToCheck.map((algo) =>
        algo.test().then(
          () => algo.name,
          (error) => {
            if (verbose)
              console.error(`Error testing algorithm ${algo.name}:`, error);
          }
        )
      );

      Promise.allSettled(checks).then((results) => {
        const measure = performance.measure(
          'crypto-support-check',
          'crypto-support-start'
        );

        if (isMounted) {
          setCryptoSupported({
            pending: false,
            supported: true,
            algorithms: results
              .map((result) =>
                result.status === 'fulfilled' ? result.value : undefined
              )
              .filter((value) => value !== undefined),
            performance: measure,
          });
        }

        resolve();
      });
    });

    return () => {
      isMounted = false;
    };
  }, [crypto, subtle, verbose]);

  return cryptoSupported;
}
